// 軽量トースト機構
//
// 用途は限定的(セッション期限切れ通知が主)なので、外部ライブラリは入れずに
// 自前で実装する。React Context + portal 風の固定配置 div で済ませる。
//
// 仕様:
// - position: 画面右下固定、z-index は最前面
// - 同時表示は最大 3 件、超過は古い順に消える
// - 自動 dismiss(default 5 秒)、手動の閉じるボタンも有り
// - level: info / warn / error の 3 段階で枠と文字色を切替
//
// 使い方:
//   <ToastProvider><App /></ToastProvider>
//   const { showToast } = useToast();
//   showToast('メッセージ', { level: 'warn', durationMs: 5000 });

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ToastLevel = 'info' | 'warn' | 'error';

export interface ToastOptions {
  level?: ToastLevel;
  durationMs?: number;
}

interface ToastRecord {
  id: number;
  message: string;
  level: ToastLevel;
  durationMs: number;
}

interface ToastContextValue {
  showToast: (message: string, opts?: ToastOptions) => number;
  dismissToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_TOASTS = 3;
const DEFAULT_DURATION_MS = 5_000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const idCounterRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismissToast = useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, opts?: ToastOptions): number => {
      idCounterRef.current += 1;
      const id = idCounterRef.current;
      const record: ToastRecord = {
        id,
        message,
        level: opts?.level ?? 'info',
        durationMs:
          typeof opts?.durationMs === 'number' && opts.durationMs > 0
            ? opts.durationMs
            : DEFAULT_DURATION_MS,
      };
      setToasts((prev) => {
        const next = [...prev, record];
        // 上限を超えた古いものは setTimeout を片付けてから捨てる
        while (next.length > MAX_TOASTS) {
          const dropped = next.shift();
          if (dropped) {
            const t = timersRef.current.get(dropped.id);
            if (t) {
              clearTimeout(t);
              timersRef.current.delete(dropped.id);
            }
          }
        }
        return next;
      });
      const timer = setTimeout(() => {
        timersRef.current.delete(id);
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, record.durationMs);
      timersRef.current.set(id, timer);
      return id;
    },
    [],
  );

  // unmount 時にタイマーを掃除
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  const value = useMemo(
    () => ({ showToast, dismissToast }),
    [showToast, dismissToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}

// ----------------------------------------------------------------
// 描画
// ----------------------------------------------------------------

function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: ToastRecord[];
  onDismiss: (id: number) => void;
}) {
  if (toasts.length === 0) return null;
  return (
    <div
      data-testid="toast-container"
      style={{
        position: 'fixed',
        right: '16px',
        bottom: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        zIndex: 9999,
        maxWidth: 'min(360px, calc(100vw - 32px))',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastRecord;
  onDismiss: (id: number) => void;
}) {
  const palette = paletteForLevel(toast.level);
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={`toast-${toast.level}`}
      style={{
        background: 'var(--color-card-strong)',
        border: `0.5px solid ${palette.border}`,
        color: palette.text,
        borderRadius: 'var(--radius-md)',
        padding: '10px 12px',
        fontSize: '11px',
        lineHeight: 1.5,
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        boxShadow: '0 6px 24px rgba(0, 0, 0, 0.4)',
      }}
    >
      <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
        {toast.message}
      </span>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="閉じる"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--color-text-tertiary)',
          cursor: 'pointer',
          fontSize: '14px',
          lineHeight: 1,
          padding: '0 2px',
        }}
      >
        ×
      </button>
    </div>
  );
}

function paletteForLevel(level: ToastLevel): { border: string; text: string } {
  switch (level) {
    case 'warn':
      return {
        border: 'rgba(251, 191, 36, 0.45)',
        text: 'var(--color-warning-text)',
      };
    case 'error':
      return {
        border: 'rgba(239, 68, 68, 0.45)',
        text: 'var(--color-error-text)',
      };
    case 'info':
    default:
      return {
        border: 'rgba(167, 139, 250, 0.45)',
        text: 'var(--color-text-accent)',
      };
  }
}
